import * as THREE from "three";
import { CardKind, Cell, LevelSim } from "@highmarch/domain";
import { CameraFollow } from "../render/cameraFollow";
import { IsoCamera } from "../render/isoCamera";
import { groundAt } from "../render/terrain";
import { reducedVfx } from "../ui/settings";

/**
 * Placement camera: the view follows what is being placed (owner, 2026-09-23: "the camera also needs to smoothly
 * follow the road as you're placing it, so it's easier to follow"). IsoCamera does the framing and the easing; this
 * decides what to frame and when, and gives the view up to the player's finger.
 */
export class PlacementCamera {
  /** The framing asked for last: a `base` change re-centres the view, a `detail` change only keeps it in view. */
  private aimBase = "";
  private aimDetail = "";
  /** Road length when the view last followed the road's end, so each new segment is followed once. */
  private aimTiles = 0;
  /** The last glide's request, re-aimed when the tray changes height under it; dropped when the player takes the view. */
  private aim: { points: readonly THREE.Vector3[]; centre: boolean; at: number; fromZoom: number } | null = null;
  /**
   * The zoom the player had before a placement stepped the view back to fit what is being placed, or null when none
   * did. The step back is the placement's, not the player's: it is undone when the card is placed, put back or
   * skipped, so the player finds the view at the zoom they chose.
   */
  private playerZoom: number | null = null;
  /** How long after a glide starts a tray resize still re-aims it. */
  static readonly ReaimMs = 1200;
  /** Tower card: how many of the nearest legal cells the view makes sure are on screen. */
  static readonly TowerCellsInView = 4;
  /** How far round a chosen tower spot is kept on screen (cells), and how high its ghost stands (world units). */
  static readonly SpotRoom = 1;
  static readonly SpotGhostHeight = 1.4;

  constructor(private readonly iso: IsoCamera) {}

  /**
   * Aims the view at the placement in hand, when it changes:
   * - a road card picked: the open end and every option, centred in the band above the tray (stepping back only if
   *   they cannot fit); ROTATE or a tap then only keeps the chosen option on screen, so cycling does not swing the view;
   * - a road segment laid: the new open end, centred, ready for the next card;
   * - a tower card picked: the legal cells nearest the view brought on screen; a chosen spot kept on screen with its
   *   ghost tower, without re-centring;
   * - an Upgrade card picked: the towers it can raise nearest the view brought on screen; the chosen one (passed as
   *   `towerSpot`, its cell) framed with room round it and its roof, like a chosen tower spot;
   * - nothing picked (an offer just shown, or the card placed, put back or skipped): the view stays where it is, bar
   *   the new open end after a segment is laid, and any step back a placement took is undone.
   */
  follow(sim: LevelSim, ghostIndex: number, towerSpot: Cell | null): void {
    const offer = sim.currentOffer, end = sim.road.openEnd;
    const picked = offer !== null && offer.hasPick ? offer.pickedCard.kind : null;
    const at = (c: Cell, lift = 0) => new THREE.Vector3(c.x, groundAt(c.x, c.y) + lift, c.y);
    if (offer !== null && picked === CardKind.Path && offer.ghosts.length > 0) {
      const i = Math.min(ghostIndex, offer.ghosts.length - 1);
      const base = `road|${offer.id}|${sim.road.designedTileCount}`;
      if (base !== this.aimBase) this.aimAt(base, `${i}`, [at(end), ...offer.ghosts.flatMap((g) => g.cells.map((c) => at(c)))], true);
      else if (`${i}` !== this.aimDetail) this.aimAt(base, `${i}`, [at(end), ...offer.ghosts[i].cells.map((c) => at(c))], false);
      this.aimTiles = sim.road.designedTileCount;
      return;
    }
    // A Move card (M55) frames like a tower card: the free squares near the view, then the chosen tower or square.
    if (offer !== null && (picked === CardKind.Tower || picked === CardKind.Upgrade || picked === CardKind.Move)) {
      const upgrade = picked === CardKind.Upgrade;
      const base = `${upgrade ? "upgrade" : picked === CardKind.Move ? "move" : "tower"}|${offer.id}`;
      if (towerSpot !== null) {
        const r = PlacementCamera.SpotRoom, s = towerSpot;
        const pts = [at(s, PlacementCamera.SpotGhostHeight), at(new Cell(s.x - r, s.y)), at(new Cell(s.x + r, s.y)), at(new Cell(s.x, s.y - r)), at(new Cell(s.x, s.y + r))];
        if (`${s.x},${s.y}` !== this.aimDetail || base !== this.aimBase) this.aimAt(base, `${s.x},${s.y}`, pts, false);
      } else if (base !== this.aimBase) {
        const look = this.iso.lookPoint();
        const d = (c: Cell) => Math.hypot(c.x - look.x, c.y - look.z);
        const cells = upgrade ? sim.towerSlots.filter((t) => sim.canUpgrade(t)).map((t) => t.cell) : sim.collectValidTowerCells();
        const near = cells.slice().sort((a, b) => d(a) - d(b));
        this.aimAt(base, "", near.slice(0, PlacementCamera.TowerCellsInView).map((c) => at(c, upgrade ? PlacementCamera.SpotGhostHeight : 0)), false);
      }
      return;
    }
    if (picked !== null) return;
    // Placement over: the next pick frames afresh (BACK then the same card again finds the view as it was left).
    const back = this.playerZoom;
    this.playerZoom = null;
    this.aimBase = ""; this.aimDetail = "";
    if (sim.road.designedTileCount !== this.aimTiles) {
      this.aimTiles = sim.road.designedTileCount;
      this.aimAt(`end|${this.aimTiles}`, "", [at(end)], true, back ?? this.iso.currentZoom);
    } else if (back !== null) {
      const look = this.iso.lookPoint();
      this.aim = null;
      this.iso.followTo({ x: look.x, z: look.z, zoom: back }, this.smoothTime());
    }
  }

  /**
   * The bottom inset to frame the view within, given the pixels a panel really covers (`covered`) and the inset it is
   * framed within now (`framed`, NaN before the first). An offer's hand — cards up, nothing picked — is dealt over
   * the view without moving it (Kevin, 2026-09-23: "as the pick-a-card screen shows, the game jumps up / back
   * down"): the view keeps its band unless the tray would cover the base or the road's open end, and then eases up
   * for it like any other panel. Every other panel (the placement bar, the tower sheet, the speed row) frames as is.
   */
  framingBottom(sim: LevelSim, covered: number, handUp: boolean, framed: number): number {
    if (!handUp || Number.isNaN(framed)) return covered;
    const h = this.iso.viewportHeight, trayTop = h - covered - CameraFollow.MarginPx;
    const hidden = [sim.grid.base, sim.road.openEnd].some((c) => {
      const y = this.iso.worldToViewport(c.x, groundAt(c.x, c.y), c.y).y;
      return y > trayTop && y <= h;                          // already off the bottom: the tray hides nothing new
    });
    return hidden ? covered : framed;
  }

  /** Road laid by something other than the player (a headless capture): nothing to follow, but nothing owed later. */
  skipRoad(sim: LevelSim): void { this.aimTiles = sim.road.designedTileCount; }

  /**
   * The screen insets moved. Picking a card morphs the tray into the shorter placement bar a layout later: the view
   * was aimed for the old tray, so aim again for the band now uncovered (the dead zone keeps this from twitching).
   */
  insetsChanged(): void {
    if (this.aim !== null && performance.now() - this.aim.at < PlacementCamera.ReaimMs) this.glide(this.aim.points, this.aim.centre, this.aim.fromZoom);
  }

  /** The player moved the view themselves: no glide carries on against their finger, and none is re-aimed. */
  take(): void {
    this.iso.cancelFollow();
    this.aim = null;
    this.playerZoom = null;                                 // the zoom is theirs now, whatever it is
  }

  /** `fromZoom`: the zoom the framing may step back from; a re-aim for a resized tray starts from the same one. */
  private aimAt(base: string, detail: string, points: readonly THREE.Vector3[], centre: boolean, fromZoom = this.iso.currentZoom): void {
    this.aimBase = base; this.aimDetail = detail;
    if (points.length === 0) return;
    this.aim = { points, centre, at: performance.now(), fromZoom };
    this.glide(points, centre, fromZoom);
  }

  private glide(points: readonly THREE.Vector3[], centre: boolean, fromZoom: number): void {
    const target = this.iso.frameTarget(points, centre, fromZoom);
    // Stepping back to fit: remember the player's zoom (the first time only — a re-aim mid-glide is not theirs).
    if (this.playerZoom === null && target.zoom > fromZoom * (1 + CameraFollow.ZoomDeadZone)) this.playerZoom = fromZoom;
    this.iso.followTo(target, this.smoothTime());
  }

  private smoothTime(): number { return reducedVfx() ? CameraFollow.ReducedSmoothTime : CameraFollow.SmoothTime; }
}
